import { Button } from '@/components/ui/button';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AnimatePresence,
  animate,
  motion,
  useMotionValue,
  useReducedMotion,
  useTransform,
  useVelocity,
  type MotionValue,
} from 'framer-motion';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { diasDesde, useComemoracao } from '@/hooks/useSequencia';

/*
 * O fogo da sequência (30/10: "uma animação bem foda, com um fogo aparecendo,
 * o número subindo, tipo do Duolingo"). Abre ao concluir a primeira aula do
 * dia e quando a pessoa toca no número da sequência.
 *
 * Coreografia (s):
 *   0,00  a tela escurece
 *   0,15  uma brasa acende na base
 *   0,35  a chama sobe da brasa, passa do tamanho e assenta; faíscas saem
 *         e uma onda de luz abre a partir da base
 *   0,55  o número de ontem aparece embaixo; a semana entra dia a dia
 *   1,10  o número gira como um hodômetro até o de hoje (com desfoque de
 *         movimento no giro), a chama estoura de novo, o "+1" sobe e o dia
 *         de hoje acende na semana (o celular vibra)
 *   1,50  o texto e o botão sobem
 * Revendo (tocou na sequência), o número gira de 0 até o total.
 *
 * O desenho da chama é o do Lucide (o mesmo fogo do ranking, FogoVivo), em
 * três línguas presas pela base, cada uma tremulando no seu ritmo.
 */

const CHAMA =
  'M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z';

const DIAS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const NOMES = [
  'domingo',
  'segunda',
  'terça',
  'quarta',
  'quinta',
  'sexta',
  'sábado',
];

/** Sorteio fixo por índice: as faíscas saem sempre do mesmo jeito. */
const sorteio = (i: number, s: number) => {
  const x = Math.sin(i * 12.9898 + s * 78.233) * 43758.5453;
  return x - Math.floor(x);
};

export function ComemoracaoDaSequencia() {
  const aberta = useComemoracao((s) => s.aberta);
  return createPortal(
    <AnimatePresence>{aberta && <Tela key="fogo" />}</AnimatePresence>,
    document.body,
  );
}

function Tela() {
  const { de, para, recorde, ultimoDia, revendo, fechar } = useComemoracao();
  const quieto = useReducedMotion();
  const valor = useMotionValue(quieto ? para : de);
  // o segundo estouro: quando o número vira
  const [virou, setVirou] = useState(!!quieto);
  const botao = useRef<HTMLButtonElement>(null);

  const momentoDoGiro = revendo ? 0.75 : 1.1;

  useEffect(() => {
    if (quieto) return;
    const t = window.setTimeout(() => {
      setVirou(true);
      animate(
        valor,
        para,
        revendo
          ? {
              duration: Math.min(1.6, 0.6 + para * 0.03),
              ease: [0.16, 1, 0.3, 1],
            }
          : // de ontem para hoje: gira, passa um tico e volta (peso de hodômetro)
            { type: 'spring', stiffness: 150, damping: 13, mass: 1 },
      );
      try {
        navigator.vibrate?.([16, 50, 30]);
      } catch {
        /* sem vibração neste aparelho */
      }
    }, momentoDoGiro * 1000);
    return () => window.clearTimeout(t);
  }, [quieto, para, revendo, valor, momentoDoGiro]);

  // Esc fecha; a página de trás não rola
  useEffect(() => {
    const tecla = (e: KeyboardEvent) => e.key === 'Escape' && fechar();
    window.addEventListener('keydown', tecla);
    const antes = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    botao.current?.focus({ preventScroll: true });
    return () => {
      window.removeEventListener('keydown', tecla);
      document.body.style.overflow = antes;
    };
  }, [fechar]);

  const estudouHoje = diasDesde(ultimoDia) === 0;
  const digitos = String(Math.max(de, para, 1)).length;
  const novo = !revendo && para === 1;

  const titulo = revendo
    ? para === 0
      ? 'O fogo está apagado'
      : estudouHoje
        ? 'Fogo aceso hoje'
        : 'Falta a aula de hoje'
    : novo
      ? 'Você começou uma sequência'
      : 'Mais um dia aceso';
  const texto = revendo
    ? para === 0
      ? 'Conclua uma aula hoje para acender uma sequência nova.'
      : estudouHoje
        ? recorde > para
          ? `Seu recorde é de ${recorde} dias. Volte amanhã para chegar lá.`
          : 'É o seu recorde. Volte amanhã para manter o fogo aceso.'
        : 'Conclua uma aula hoje para o fogo não apagar.'
    : novo
      ? 'Conclua uma aula amanhã para o fogo crescer.'
      : 'Conclua uma aula amanhã para manter o fogo aceso.';

  return (
    <motion.div
      role="dialog"
      aria-modal="true"
      aria-labelledby="fogo-titulo"
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto px-6 py-10"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.25, delay: 0.05 } }}
      transition={{ duration: 0.3 }}
      onClick={fechar}
    >
      <div className="bg-background/90 absolute inset-0 backdrop-blur-md" />
      {/* a luz nasce na base da chama e abre para cima */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(60% 45% at 50% 38%, rgba(249,115,22,0.22), rgba(239,68,68,0.06) 45%, transparent 70%)',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: virou ? [0.7, 1, 0.85] : 0.7 }}
        transition={{ duration: 0.6, delay: virou ? 0 : 0.35 }}
      />

      <motion.div
        className="relative flex w-full max-w-sm flex-col items-center text-center"
        onClick={(e) => e.stopPropagation()}
        exit={{ scale: 0.94, opacity: 0, transition: { duration: 0.22 } }}
      >
        <ChamaGrande virou={virou} quieto={!!quieto} apagada={para === 0} />

        <div className="relative -mt-2">
          <motion.div
            className="font-black leading-none tracking-tight text-orange-500"
            style={{ fontSize: 'clamp(4.5rem, 22vw, 6.5rem)' }}
            initial={quieto ? false : { opacity: 0, y: 16, scale: 0.8 }}
            animate={{
              opacity: 1,
              y: 0,
              scale: virou && !quieto ? [1, 1.12, 1] : 1,
            }}
            transition={{
              opacity: { duration: 0.3, delay: 0.55 },
              y: { type: 'spring', stiffness: 380, damping: 22, delay: 0.55 },
              scale: virou
                ? { duration: 0.45, ease: [0.22, 1.4, 0.36, 1] }
                : { type: 'spring', stiffness: 380, damping: 22, delay: 0.55 },
            }}
          >
            <Hodometro valor={valor} digitos={digitos} />
          </motion.div>

          {/* o "+1" que sobe quando o número vira */}
          {!revendo && !quieto && para > de && (
            <motion.span
              aria-hidden
              className="absolute -right-8 top-1 rounded-full bg-orange-500 px-2 py-0.5 text-sm font-black text-white shadow-lg"
              initial={{ opacity: 0, y: 10, scale: 0.6 }}
              animate={
                virou
                  ? {
                      opacity: [0, 1, 1, 0],
                      y: [10, -6, -22, -40],
                      scale: [0.6, 1.1, 1, 0.9],
                    }
                  : { opacity: 0 }
              }
              transition={{
                duration: 1.3,
                times: [0, 0.2, 0.7, 1],
                delay: 0.15,
              }}
            >
              +{para - de}
            </motion.span>
          )}
        </div>

        <motion.p
          className="text-foreground mt-1 text-lg font-bold"
          initial={quieto ? false : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65, duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        >
          {para === 1 ? 'dia seguido' : 'dias seguidos'}
        </motion.p>

        <Semana
          para={para}
          ultimoDia={ultimoDia}
          revendo={revendo}
          momentoDoGiro={momentoDoGiro}
          quieto={!!quieto}
        />

        <motion.div
          initial={quieto ? false : { opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            delay: momentoDoGiro + 0.4,
            duration: 0.45,
            ease: [0.16, 1, 0.3, 1],
          }}
          className="mt-7 w-full"
        >
          <h2 id="fogo-titulo" className="text-xl font-extrabold">
            {titulo}
          </h2>
          <p className="text-muted-foreground mx-auto mt-1.5 max-w-xs text-sm leading-relaxed">
            {texto}
          </p>
          <Button
            ref={botao}
            type="button"
            onClick={fechar}
            className="mt-6 w-full"
          >
            Continuar
          </Button>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

/** A chama: brasa → sobe, passa do tamanho e assenta; estoura de novo ao virar. */
function ChamaGrande({
  virou,
  quieto,
  apagada,
}: {
  virou: boolean;
  quieto: boolean;
  apagada: boolean;
}) {
  return (
    <div className="relative h-44 w-44" aria-hidden>
      {/* onda de luz que abre da base, na chegada e na virada */}
      {!quieto && <Onda key="chegada" atraso={0.35} />}
      {!quieto && virou && <Onda key="virada" atraso={0.02} />}

      {/* faíscas: na chegada e de novo na virada */}
      {!quieto && (
        <Faiscas key="faiscas-chegada" atraso={0.38} quantas={12} semente={1} />
      )}
      {!quieto && virou && (
        <Faiscas key="faiscas-virada" atraso={0.02} quantas={16} semente={2} />
      )}

      {/* brasas subindo devagar enquanto a tela está aberta */}
      {!quieto && !apagada && (
        <div className="absolute inset-x-8 bottom-10 top-0">
          {Array.from({ length: 7 }, (_, i) => (
            <span
              key={i}
              className="brasa-sobe absolute bottom-0 h-1.5 w-1.5 rounded-full bg-amber-300"
              style={{
                left: `${10 + sorteio(i, 3) * 80}%`,
                animationDelay: `${0.9 + sorteio(i, 4) * 2.2}s`,
                animationDuration: `${1.8 + sorteio(i, 5) * 1.4}s`,
              }}
            />
          ))}
        </div>
      )}

      {/* brasa que acende antes da chama */}
      <motion.span
        className="absolute bottom-7 left-1/2 -ml-5 h-5 w-10 rounded-[50%] bg-orange-500 blur-md"
        initial={quieto ? false : { scale: 0, opacity: 0 }}
        animate={{ scale: [0, 1.2, 1], opacity: apagada ? 0.25 : [0, 1, 0.75] }}
        transition={{ duration: 0.5, delay: 0.15 }}
      />

      <motion.svg
        viewBox="0 0 24 24"
        className={cn(
          'absolute inset-0 h-full w-full overflow-visible drop-shadow-[0_10px_30px_rgba(249,115,22,0.45)]',
          apagada && 'opacity-30 grayscale',
        )}
        style={{ originX: 0.5, originY: 0.92 }}
        initial={quieto ? false : { scaleY: 0, scaleX: 0.5, opacity: 0 }}
        animate={
          virou && !quieto
            ? {
                scaleY: [1, 1.22, 0.94, 1],
                scaleX: [1, 0.92, 1.05, 1],
                opacity: 1,
              }
            : { scaleY: 1, scaleX: 1, opacity: 1 }
        }
        transition={
          virou
            ? { duration: 0.6, ease: [0.22, 1.2, 0.36, 1] }
            : {
                type: 'spring',
                stiffness: 260,
                damping: 13,
                mass: 0.9,
                delay: 0.32,
              }
        }
      >
        <g className="fogo-lingua fogo-fora">
          <path d={CHAMA} fill="#ef4444" />
        </g>
        <g transform="translate(12 22) scale(.76) translate(-12 -22)">
          <g className="fogo-lingua fogo-meio">
            <path d={CHAMA} fill="#f97316" />
          </g>
        </g>
        <g transform="translate(12 22) scale(.5) translate(-12 -22)">
          <g className="fogo-lingua fogo-miolo">
            <path d={CHAMA} fill="#fde047" />
          </g>
        </g>
        <g transform="translate(12 22) scale(.26) translate(-12 -22)">
          <g className="fogo-lingua fogo-meio">
            <path d={CHAMA} fill="#fffbeb" />
          </g>
        </g>
      </motion.svg>
    </div>
  );
}

function Onda({ atraso }: { atraso: number }) {
  return (
    <motion.span
      className="absolute bottom-6 left-1/2 -ml-8 h-16 w-16 rounded-full border-2 border-orange-400/70"
      initial={{ scale: 0.2, opacity: 0 }}
      animate={{ scale: [0.2, 3.2], opacity: [0, 0.9, 0] }}
      transition={{ duration: 0.9, delay: atraso, ease: [0.16, 1, 0.3, 1] }}
    />
  );
}

function Faiscas({
  atraso,
  quantas,
  semente,
}: {
  atraso: number;
  quantas: number;
  semente: number;
}) {
  return (
    <div className="pointer-events-none absolute bottom-10 left-1/2 h-0 w-0">
      {Array.from({ length: quantas }, (_, i) => {
        // para cima e para os lados (de -160° a -20°)
        const angulo = ((-160 + sorteio(i, semente) * 140) * Math.PI) / 180;
        const distancia = 70 + sorteio(i, semente + 7) * 80;
        const tam = 3 + sorteio(i, semente + 3) * 4;
        return (
          <motion.span
            key={i}
            className={cn(
              'absolute rounded-full',
              i % 3 === 0
                ? 'bg-yellow-300'
                : i % 3 === 1
                  ? 'bg-orange-400'
                  : 'bg-red-400',
            )}
            style={{
              width: tam,
              height: tam,
              marginLeft: -tam / 2,
              marginTop: -tam / 2,
            }}
            initial={{ x: 0, y: 0, opacity: 0, scale: 1 }}
            animate={{
              x: Math.cos(angulo) * distancia,
              // sobe e depois cai um pouco (gravidade)
              y: [
                0,
                Math.sin(angulo) * distancia,
                Math.sin(angulo) * distancia + 18,
              ],
              opacity: [0, 1, 0],
              scale: [1, 1, 0.2],
            }}
            transition={{
              duration: 0.75 + sorteio(i, semente + 9) * 0.45,
              delay: atraso + sorteio(i, semente + 5) * 0.08,
              ease: [0.16, 1, 0.3, 1],
            }}
          />
        );
      })}
    </div>
  );
}

/** Número que gira como hodômetro: cada casa vira quando a de baixo passa de 9. */
function Hodometro({
  valor,
  digitos,
}: {
  valor: MotionValue<number>;
  digitos: number;
}) {
  const velocidade = useVelocity(valor);
  // desfoque de movimento: acompanha a velocidade e some parado
  const desfoque = useTransform(velocidade, (v) => {
    const px = Math.min(Math.abs(v) / 14, 3);
    return px < 0.15 ? 'none' : `blur(${px.toFixed(2)}px)`;
  });
  return (
    <span className="inline-flex tabular-nums" aria-hidden>
      {Array.from({ length: digitos }, (_, k) => (
        <Casa
          key={k}
          valor={valor}
          potencia={10 ** (digitos - 1 - k)}
          desfoque={desfoque}
        />
      ))}
    </span>
  );
}

function Casa({
  valor,
  potencia,
  desfoque,
}: {
  valor: MotionValue<number>;
  potencia: number;
  desfoque: MotionValue<string>;
}) {
  // posição contínua de 0 a 10 na fita de dígitos (o 10 é o 0 de novo)
  const posicao = useTransform(valor, (v) => {
    const n = Math.max(0, v);
    if (potencia === 1) return n % 10;
    const inteiro = Math.floor(n / potencia);
    // a casa de cima só anda enquanto a de baixo vai do 9 para o 0
    const virando = Math.max(0, (n % potencia) - (potencia - 1));
    return (inteiro % 10) + virando;
  });
  const y = useTransform(posicao, (p) => `${-p}em`);
  // casa da esquerda sem valor ainda (o 0 de 09) não aparece nem ocupa lugar
  const presenca = useTransform(valor, (v) =>
    potencia === 1 ? 1 : Math.min(1, Math.max(0, v - (potencia - 1))),
  );
  const largura = useTransform(presenca, (p) => `${(p * 0.6).toFixed(3)}em`);
  return (
    <motion.span
      className="relative inline-block h-[1em] overflow-hidden"
      style={{ width: potencia === 1 ? '0.6em' : largura, opacity: presenca }}
    >
      <motion.span
        className="absolute inset-x-0 top-0 flex flex-col items-center"
        style={{ y, filter: desfoque }}
      >
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map((d, i) => (
          <span key={i} className="block h-[1em] leading-none">
            {d}
          </span>
        ))}
      </motion.span>
    </motion.span>
  );
}

/** A semana, de domingo a sábado: os dias da sequência acesos; hoje acende na virada. */
function Semana({
  para,
  ultimoDia,
  revendo,
  momentoDoGiro,
  quieto,
}: {
  para: number;
  ultimoDia: string | null;
  revendo: boolean;
  momentoDoGiro: number;
  quieto: boolean;
}) {
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const fim = ultimoDia ? new Date(ultimoDia) : null;
  fim?.setHours(0, 0, 0, 0);
  const inicio = fim ? new Date(fim.getTime() - (para - 1) * 86_400_000) : null;
  const domingo = new Date(hoje.getTime() - hoje.getDay() * 86_400_000);

  return (
    <motion.ul
      className="border-border/60 bg-card/70 mt-6 flex w-full justify-between rounded-2xl border px-3 py-3"
      initial={quieto ? false : { opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.7, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
    >
      {DIAS.map((letra, i) => {
        const dia = new Date(domingo.getTime() + i * 86_400_000);
        const ehHoje = dia.getTime() === hoje.getTime();
        const aceso =
          !!inicio && !!fim && dia >= inicio && dia <= fim && para > 0;
        // o dia que acende agora espera o número virar
        const acendeAgora = aceso && ehHoje && !revendo;
        const atraso = acendeAgora ? momentoDoGiro + 0.1 : 0.75 + i * 0.06;
        return (
          <li
            key={i}
            className="flex flex-col items-center gap-1.5"
            aria-label={`${NOMES[i]}${aceso ? ', estudou' : ''}`}
          >
            <span
              className={cn(
                'text-xs font-bold',
                ehHoje ? 'text-orange-500' : 'text-muted-foreground',
              )}
            >
              {letra}
            </span>
            <span
              className={cn(
                'relative flex h-9 w-9 items-center justify-center rounded-full border-2',
                ehHoje ? 'border-orange-500/60' : 'border-border',
              )}
            >
              {aceso && (
                <motion.span
                  className="absolute inset-[-2px] flex items-center justify-center rounded-full bg-orange-500 text-white"
                  initial={quieto ? false : { scale: 0, opacity: 0 }}
                  animate={{
                    scale: acendeAgora ? [0, 1.25, 1] : 1,
                    opacity: 1,
                  }}
                  transition={{
                    delay: atraso,
                    duration: acendeAgora ? 0.5 : 0.3,
                    ease: [0.22, 1.2, 0.36, 1],
                  }}
                >
                  <motion.span
                    initial={quieto ? false : { scale: 0, rotate: -30 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{
                      delay: atraso + 0.12,
                      type: 'spring',
                      stiffness: 500,
                      damping: 18,
                    }}
                  >
                    <Check className="h-4 w-4" strokeWidth={3.5} />
                  </motion.span>
                </motion.span>
              )}
            </span>
          </li>
        );
      })}
    </motion.ul>
  );
}
